import { useMemo, useState } from "react";

import useLeadDocuments from "../../data/useLeadDocuments";
import { DocumentType, IDocument } from "../../models/Document";
import { ILead } from "../../models/Lead";
import classNames from "../../utils/classNames";
import Card from "../shared/Card/Card";
import ImageCarousel from "../shared/Images/ImageCarousel";
import LoadingWheel from "../shared/LoadingWheel";

type FilterGroup = {
  key: string;
  label: string;
  match: (doc: IDocument) => boolean;
};

// Grouped document-type filters shown above the carousel.
const FILTER_GROUPS: FilterGroup[] = [
  { key: "all", label: "All", match: () => true },
  {
    key: "vehicle",
    label: "Vehicle",
    match: (doc) => doc.type === DocumentType.Vehicle || doc.type === DocumentType.Misc,
  },
  {
    key: "customer",
    label: "Customer",
    match: (doc) => doc.uploadedByCustomer || doc.type === DocumentType.Customer,
  },
  {
    key: "compliance",
    label: "Compliance",
    match: (doc) =>
      doc.type === DocumentType.COD ||
      doc.type === DocumentType.Identity ||
      doc.type === DocumentType.CollectionNote,
  },
];

// Keeps the most useful captures (engine video, front, odometer) at the front of the carousel.
const sortImages = (docs: IDocument[]) => {
  const front = docs.filter((doc) => doc.fileName?.includes("front"));
  const odometer = docs.filter((doc) => doc.fileName?.includes("odometer"));
  const engineVideo = docs.filter((doc) => doc.fileName?.includes("engine-video"));
  const rest = docs.filter(
    (doc) =>
      !doc.fileName?.includes("front") &&
      !doc.fileName?.includes("odometer") &&
      !doc.fileName?.includes("engine-video")
  );

  return [...engineVideo, ...front, ...odometer, ...rest];
};

export default function VehicleImagesCard({ lead }: { lead?: ILead | null }) {
  const { leadDocuments } = useLeadDocuments(lead?.id ?? "", "VehicleImagesCard");
  const [activeFilter, setActiveFilter] = useState("all");

  const documents = useMemo<IDocument[]>(() => leadDocuments.data?.data ?? [], [leadDocuments.data]);

  const visibleGroups = useMemo(
    () => FILTER_GROUPS.filter((group) => group.key === "all" || documents.some(group.match)),
    [documents]
  );

  const activeGroup = visibleGroups.find((group) => group.key === activeFilter) ?? visibleGroups[0];

  const images = useMemo(() => {
    if (!activeGroup) return [];
    return sortImages(documents.filter(activeGroup.match));
  }, [documents, activeGroup]);

  if (lead?.id && leadDocuments.isLoading) {
    return (
      <Card title="Vehicle Images" bodyClassName="p-4">
        <LoadingWheel />
      </Card>
    );
  }

  return (
    <Card title="Vehicle Images" bodyClassName="p-4" collapsible>
      <div className="mb-4 flex flex-wrap gap-2">
        {visibleGroups.map((group) => {
          const count = documents.filter(group.match).length;
          const isActive = group.key === activeGroup?.key;
          return (
            <button
              key={group.key}
              type="button"
              onClick={() => setActiveFilter(group.key)}
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                isActive
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              )}
            >
              <span>{group.label}</span>
              <span
                className={classNames(
                  "rounded-full px-1.5 text-[11px] font-semibold",
                  isActive ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-500"
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {images.length > 0 ? (
        <ImageCarousel images={images} leadId={lead?.id} />
      ) : (
        <p className="py-8 text-center text-sm text-gray-500">No images {activeFilter === "all" ? " found" : ` for ${activeFilter}` }</p>
      )}
    </Card>
  );
}
